import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Wallet, ArrowDownLeft, ArrowUpRight, Copy, Check, LogOut, X } from 'lucide-react';
import { useTonConnectUI, useTonAddress } from '@tonconnect/ui-react';
import { beginCell, Address } from '@ton/core'; // نیاز به نصب پکیج @ton/core داری

const HOUSE_WALLET_ADDRESS = 'UQD0IyBb75MRBCWORh4wHPhVdyUTmbzCpcN_Gw5fzXeTZIsg'; //[cite: 8]
// آدرس رسمی USDT روی شبکه TON (Mainnet)
const USDT_MASTER_ADDRESS = 'EQCxE6mUtQJKFnGfaROTKOt1lZbDiiX1kCixRv7Nw2Id_sDs';

const ProfileTab = ({ user }) => {
  const { t } = useTranslation(); //[cite: 8]
  const [tonConnectUI] = useTonConnectUI(); //[cite: 8]
  const userFriendlyAddress = useTonAddress(); //[cite: 8]

  const [copied, setCopied] = useState(false); //[cite: 8]
  const [showDepositModal, setShowDepositModal] = useState(false); //[cite: 8]
  const [depositAmount, setDepositAmount] = useState('10'); //[cite: 8]

  const handleWalletClick = () => { //[cite: 8]
    if (userFriendlyAddress) { //[cite: 8]
      tonConnectUI.disconnect(); //[cite: 8]
    } else {
      tonConnectUI.openModal(); //[cite: 8]
    }
  };

  const copyToClipboard = (text) => { //[cite: 8]
    navigator.clipboard.writeText(text); //[cite: 8]
    setCopied(true); //[cite: 8]
    setTimeout(() => setCopied(false), 2000); //[cite: 8]
  };

  // ساخت Payload استاندارد برای انتقال USDT (TEP-74 Jetton Standard)
  const createUsdtTransferPayload = (recipientAddress, amountInUsdt, userId) => {
    // USDT دارای 6 اعشار است
    const amountInUnits = globalThis.BigInt(Math.floor(parseFloat(amountInUsdt) * 1e6));

    return beginCell()
      .storeUint(0x0f8810ea, 32) // Opcode برای Jetton Transfer
      .storeUint(0, 64)          // query_id
      .storeCoins(amountInUnits) // مقدار USDT
      .storeAddress(Address.parse(recipientAddress)) // آدرس ولت مقصد (خانه)
      .storeAddress(Address.parse(userFriendlyAddress)) // آدرس ولت مبدا برای دریافت پاسخ
      .storeBit(0)               // custom_payload (بدون payload سفارشی)
      .storeCoins(globalThis.BigInt(1))     // forward_ton_amount (حداقل کارمزد برای اطلاع به مقصد)
      .storeBit(0)               // forward_payload (می‌توان شناسه کاربر را اینجا اضافه کرد)
      .endCell()
      .toBoc()
      .toString('base64');
  };

  const handleSendDeposit = async () => {
    if (!depositAmount || parseFloat(depositAmount) <= 0 || !userFriendlyAddress) return;

    try {
      const payload = createUsdtTransferPayload(HOUSE_WALLET_ADDRESS, depositAmount, user?.id);

      const transaction = {
        validUntil: Math.floor(Date.now() / 1000) + 600, //[cite: 8]
        messages: [
          {
            // درخواست باید به آدرس Master/Wallet کنترلی ارسال شود
            address: USDT_MASTER_ADDRESS, 
            amount: '50000000', // حدود 0.05 TON برای کارمزد گس شبکه‌ی Jetton Transfer
            payload: payload,
          },
        ],
      };

      await tonConnectUI.sendTransaction(transaction); //[cite: 8]
      alert(t('betSuccess')); //[cite: 8]
      setShowDepositModal(false); //[cite: 8]
    } catch (err) {
      console.error('USDT Deposit error:', err);
    }
  };

  const formatAddress = (addr) => { //[cite: 8]
    if (!addr) return ''; //[cite: 8]
    return `${addr.slice(0, 6)}...${addr.slice(-4)}`; //[cite: 8]
  };

  return (
    // همان JSX کد خودت...
    <main className="p-4 flex-1 flex flex-col gap-4">
      {/* Profile Card */}
      <div className="bg-[#1A1528] p-6 rounded-3xl border border-white/5 text-center relative overflow-hidden">
        <div className="w-16 h-16 bg-purple-600/20 text-purple-400 rounded-full flex items-center justify-center mx-auto mb-3 border border-purple-500/30 text-2xl font-bold">
          {user?.first_name ? user.first_name[0] : 'U'}
        </div>
        <h3 className="font-bold text-lg text-white">{user?.first_name || 'Telegram User'}</h3>
        <p className="text-xs text-gray-400 font-mono mt-0.5">@{user?.id || 'user'}</p>

        <div className="mt-6 pt-6 border-t border-white/5 flex justify-between items-center">
          <span className="text-xs text-gray-400">{t('balance')}:</span>
          <span className="font-mono font-bold text-emerald-400 text-xl">
            ${parseFloat(user?.balance || 0).toFixed(2)} USDT
          </span>
        </div>
      </div>

      {!userFriendlyAddress ? (
        <button
          onClick={handleWalletClick}
          className="w-full py-4 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white font-extrabold rounded-2xl shadow-lg shadow-purple-600/25 flex items-center justify-center gap-2 transition-all active:scale-[0.98]"
        >
          <Wallet size={20} />
          <span>{t('wallet')}</span>
        </button>
      ) : (
        <div className="flex flex-col gap-3">
          <div className="bg-[#1A1528] p-3.5 rounded-2xl border border-white/5 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse" />
              <span className="text-xs text-gray-400 font-medium">{t('walletAddress')}:</span>
              <span className="text-xs text-purple-300 font-mono font-bold">
                {formatAddress(userFriendlyAddress)}
              </span>
            </div>
            <button
              onClick={handleWalletClick}
              className="text-gray-400 hover:text-rose-400 p-1.5 transition-colors"
              title={t('disconnect')}
            >
              <LogOut size={16} />
            </button>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => setShowDepositModal(true)}
              className="py-3.5 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 rounded-2xl font-bold text-sm flex items-center justify-center gap-2 transition-all active:scale-[0.98]"
            >
              <ArrowDownLeft size={18} />
              <span>{t('deposit')}</span>
            </button>

            <button
              onClick={() => alert('Withdraw feature coming soon!')}
              className="py-3.5 bg-purple-500/10 hover:bg-purple-500/20 text-purple-300 border border-purple-500/30 rounded-2xl font-bold text-sm flex items-center justify-center gap-2 transition-all active:scale-[0.98]"
            >
              <ArrowUpRight size={18} />
              <span>{t('withdraw')}</span>
            </button>
          </div>
        </div>
      )}

      {showDepositModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-[#1A1528] border border-white/10 rounded-3xl w-full max-w-sm p-5 flex flex-col gap-4 relative animate-in fade-in zoom-in-95 duration-200">
            <button
              onClick={() => setShowDepositModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-white"
            >
              <X size={20} />
            </button>

            <h4 className="font-extrabold text-white text-lg flex items-center gap-2">
              <ArrowDownLeft className="text-emerald-400" size={22} />
              {t('deposit')}
            </h4>

            <p className="text-xs text-gray-400">{t('depositDesc')}</p>

            <div className="flex flex-col gap-1.5">
              <label className="text-[11px] text-gray-400 font-medium">{t('amountToDeposit')}</label>
              <input
                type="number"
                value={depositAmount}
                onChange={(e) => setDepositAmount(e.target.value)}
                className="bg-[#120F1D] border border-white/10 rounded-xl px-3 py-2.5 text-white font-mono font-bold focus:outline-none focus:border-purple-500"
                placeholder="10"
              />
            </div>

            <div className="bg-[#120F1D] p-3 rounded-xl border border-white/5 flex items-center justify-between">
              <span className="text-xs font-mono text-gray-300 truncate mr-2">
                {HOUSE_WALLET_ADDRESS}
              </span>
              <button
                onClick={() => copyToClipboard(HOUSE_WALLET_ADDRESS)}
                className="text-purple-400 hover:text-purple-300 shrink-0"
              >
                {copied ? <Check size={16} className="text-emerald-400" /> : <Copy size={16} />}
              </button>
            </div>

            <button
              onClick={handleSendDeposit}
              className="w-full py-3.5 bg-emerald-500 hover:bg-emerald-400 text-black font-extrabold rounded-xl shadow-lg shadow-emerald-500/20 transition-all active:scale-[0.98]"
            >
              {t('sendTransaction')}
            </button>
          </div>
        </div>
      )}
    </main>
  );
};

export default ProfileTab;
